/**
 * Date/time formatting helpers.
 *
 * Pure functions with no dependencies — the app ships no date library, and this
 * is the single place that decides how timestamps read to the user.
 *
 * `formatAbsoluteDate` matches the format already used inline by ProjectsPage
 * and HomePage so those call sites can migrate onto it.
 */

/** Rendered when a timestamp is missing or unparseable. */
const UNKNOWN = "Unknown";

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Parses an ISO-8601 string into a timestamp, or `undefined` when absent or
 * invalid. Server timestamps are trusted but optional throughout the API, so
 * every formatter here has to tolerate both.
 */
const parse = (dateStr: string | undefined): number | undefined => {
  if (!dateStr) return undefined;
  const ms = new Date(dateStr).getTime();
  return Number.isNaN(ms) ? undefined : ms;
};

/**
 * Formats a timestamp as an absolute date, e.g. `Aug 24, 2026`.
 *
 * @param dateStr - ISO-8601 timestamp
 * @returns the formatted date, or "Unknown" when absent/invalid
 */
export const formatAbsoluteDate = (dateStr?: string): string => {
  const ms = parse(dateStr);
  if (ms === undefined) return UNKNOWN;
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Formats a timestamp as a short relative age, e.g. `Just now`, `12 minutes
 * ago`, `2 hours ago`, `Yesterday`, `3 days ago`. Beyond a week it falls back to
 * an absolute date, because "37 days ago" is harder to place than a date.
 *
 * Future timestamps (clock skew between the browser and the server) read as
 * "Just now" rather than a negative age.
 *
 * @param dateStr - ISO-8601 timestamp
 * @param now - injectable current time, for tests
 * @returns the formatted age, or "Unknown" when absent/invalid
 */
export const formatRelativeTime = (
  dateStr?: string,
  now: number = Date.now(),
): string => {
  const ms = parse(dateStr);
  if (ms === undefined) return UNKNOWN;

  const elapsed = now - ms;
  if (elapsed < MINUTE_MS) return "Just now";

  if (elapsed < HOUR_MS) {
    const minutes = Math.floor(elapsed / MINUTE_MS);
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }

  if (elapsed < DAY_MS) {
    const hours = Math.floor(elapsed / HOUR_MS);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.floor(elapsed / DAY_MS);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;

  return formatAbsoluteDate(dateStr);
};

/**
 * Formats a byte count for display, e.g. `840 KB`, `2.4 MB`.
 *
 * Uses decimal units (KB = 1000 B) to match what operating systems and S3
 * consoles show users, and one decimal place only where it adds information.
 *
 * @param bytes - size in bytes
 * @returns the formatted size, or an empty string when absent
 */
export const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined || bytes < 0) return "";
  if (bytes < 1000) return `${bytes} B`;

  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1000;
  let unitIndex = 0;

  while (value >= 1000 && unitIndex < units.length - 1) {
    value /= 1000;
    unitIndex++;
  }

  // One decimal below 10 (2.4 MB), none above (840 KB) — the extra digit stops
  // carrying useful information once the integer part is large.
  let rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);

  // Rounding can carry a value up to a full 1000 of its own unit: 999,960 bytes
  // is 999.96 KB, which the loop leaves alone because it is under 1000, and then
  // rounds to "1000 KB". Promote once more so it reads "1 MB". Left alone at the
  // largest unit, where there is nothing to promote to.
  if (rounded >= 1000 && unitIndex < units.length - 1) {
    rounded = 1;
    unitIndex++;
  }

  return `${rounded} ${units[unitIndex]}`;
};
